// useAdsCampaignConfig.js

import { useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslationApp } from "../../../lib/i18next";
import {
  BUTTON_SELECT_CONTENT,
  CREATE_ADS_CAMPAIGN_APP_ID_QUESTION,
  CREATE_ADS_CAMPAIGN_APP_PROMOTION_QUESTION,
  CREATE_ADS_CAMPAIGN_BUDGET_DAILY_QUESTION,
  CREATE_ADS_CAMPAIGN_BUDGET_DESCRIPTION_QUESTION,
  CREATE_ADS_CAMPAIGN_BUDGET_TOTAL_QUESTION,
  CREATE_ADS_CAMPAIGN_DESTINATION_URL_QUESTION,
  CREATE_ADS_CAMPAIGN_IDENTITIES_ACCOUNTS_QUESTION,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_WITH_OUT_CAROUSEL,
  CREATE_ADS_CAMPAIGN_MEDIA_URL_QUESTION,
  CREATE_ADS_CAMPAIGN_OBJECTIVE_DESCRIPTION,
  CREATE_ADS_CAMPAIGN_OBJECTIVE_QUESTION,
  CREATE_ADS_CAMPAIGN_START_END_DATE_QUESTION,
  CREATE_ADS_CAMPAIGN_TRACKING_QUESTION,
  CREATE_ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_QUESTION,
  CREATE_ADS_CAMPAIGN_TIKTOK_LEAD_DESTINATION_QUESTION,
  CREATE_ADS_CAMPAIGN_TYPE_BUDGET_QUESTION,
  CREATE_ADS_CAMPAIGN_TYPE_QUESTION,
  CREATE_ADS_CAMPAIGN_CATALOG_QUESTION,
  FIELD_REQUIRED,
  FIELDS_REQUIRED,
  START_DATE_AND_END_DATE_VALIDATION,
  FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR,
  FIELD_URL_VALID_WITH_PROTOCOL,
} from "../../../i18n/keysTranslations";
import { useDispatchApp } from "../../../lib/redux";
import {
  ADS_CAMPAIGN_APP_PROMOTION_TYPE_APP_INSTALL_OPTION,
  ADS_CAMPAIGN_APP_PROMOTION_TYPE_RETARGETING_OPTION,
  ADS_CAMPAIGN_CAROUSEL_FORMAT,
  ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
  ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION,
  ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_TIKTOK,
  ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION,
  ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_WEBSITE_OPTION,
  ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_EXTERNAL_WEBSITE,
  ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_INSTANT_PAGE,
  ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION,
  ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
  ADS_TIKTOK_CAMPAIGN_OBJECTIVES_VALID_WITH_IMAGES,
  MIN_DIMENSIONS_CONTENT,
  TIKTOK,
  TIKTOK_IMAGE_FORMATS,
  VIDEO_FORMATS,
} from "../../../utils/constants";
import { useCreditActionCosts } from "../../useCreditActionCosts";
import IconButtonAddContent from "../../../components/Buttons/IconButtonAddContent";
import { getPathDatabaseByRole } from "../../../actions/auth";
import {
  formatMediaUrl,
  formatMediaUrlAdFromCreatify,
  getMinimumBudget,
} from "../../../services/adsCampaigns";
import {
  getCurrencyAdAccountPlatform,
  getIsBlockedActions,
} from "../../../actions/getters";
import { getExtensionFile } from "../../../utils/string";
import _ from "lodash";
import { REGEX_URL_WITH_PROTOCOL_REQUIRED } from "../../../utils/regex";
import { usePlatformDefaultsInGenerateCampaign } from "../../platformDefaults/usePlatformDefaultsInGenerateCampaign";
import { useJumpToLastVisibleStep } from "../useJumpToLastVisibleStep";

const RESUME_APP_PROMOTION_ALIAS = { appPromotion: "osTarget" };
import { saveInitialPlatformDefaultsFromCampaign } from "../../platformDefaults/saveInitialPlatformDefaultsFromCampaign";

export function useGenerateCampaignFormTikTok({
  campaignID,
  generatedPlatforms = {},
  language,
  campaignContext,
  onCallbackSave = () => {},
  onExitToPlatformSelection,
  verifyBlockAlert,
  resumeValues = null,
}) {
  const STEPS = [
    "identities",
    "objective",
    "type",
    "appPromotion",
    "appID",
    "leadDestination",
    "trackingID",
    "catalogID",
    "destinationURL",
    "typeBudget",
    "dates",
    "budget",
    "mediaUrls",
  ];
  const [error, setError] = useState(null);
  const { control, watch, setValue, getValues } = useForm({
    defaultValues: {
      step: 0,
      objective: "",
      type: "",
      integrationID: "",
      adAccountID: "",
      identityTikTokID: "",
      trackingID: "",
      trackingCustomEventID: "",
      catalogID: "",
      destinationURL: "",
      destinationType: "",
      instantForm: null,
      typeBudget: "",
      budget: "",
      startDate: null,
      endDate: null,
      mediaUrls: [],
      appID: "",
      subObjective: "",
      isIOS14Campaign: true,
    },
  });

  const { isLoadingDefaults, resumeSettled } =
    usePlatformDefaultsInGenerateCampaign({
      platform: TIKTOK,
      setValue,
      getValues,
      resumeValues,
      resumeFieldAliases: RESUME_APP_PROMOTION_ALIAS,
    });

  const step = watch("step");
  const objective = watch("objective");
  const typeBudget = watch("typeBudget");
  const integrationID = watch("integrationID");
  const adAccountID = watch("adAccountID");
  const mediaUrls = watch("mediaUrls");
  const type = watch("type");
  const destinationType = watch("destinationType");
  const isInstantFormLead =
    objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION &&
    destinationType === ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_INSTANT_PAGE;
  const utils = {
    control,
    setValue,
    watch,
    getValues,
  };

  const isSalesWithApp =
    objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION &&
    type === ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION;

  const isCarouselAdsAvailable =
    ADS_TIKTOK_CAMPAIGN_OBJECTIVES_VALID_WITH_IMAGES.includes(objective) &&
    !isSalesWithApp;

  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const { ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION } = useCreditActionCosts();

  const { userID } = dispatch(getPathDatabaseByRole());

  const currency = dispatch(
    getCurrencyAdAccountPlatform({
      platform: TIKTOK,
      integrationID: integrationID,
      adAccountID: adAccountID,
    })
  );
  const actionsToValidAnswer = () => {
    setError(null);
  };

  const filterMediaUrls = () => {
    if (_.isEmpty(mediaUrls)) return;

    const mediaUrlsFiltered = mediaUrls.filter((mediaUrl) => {
      const extension = getExtensionFile(mediaUrl.url);
      const formatsAllowed = VIDEO_FORMATS.concat(
        isCarouselAdsAvailable ? TIKTOK_IMAGE_FORMATS : []
      );
      return formatsAllowed.includes(extension);
    });
    setValue("mediaUrls", mediaUrlsFiltered);
  };

  const onFinished = async () => {
    const formValues = getValues();

    const isSalesObjective =
      formValues.objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION;
    const isInstantFormRequest =
      formValues.objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION &&
      formValues.destinationType ===
        ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_INSTANT_PAGE;
    const requestObject = {
      objective: formValues.objective,
      campaignType: isSalesObjective ? formValues.type : undefined,
      integrationID: formValues.integrationID,
      adAccountID: formValues.adAccountID,
      identityTikTokID: formValues.identityTikTokID,
      destinationType:
        formValues.objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION
          ? formValues.destinationType ||
            ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_EXTERNAL_WEBSITE
          : undefined,
      instantForm: isInstantFormRequest ? formValues.instantForm : null,
      trackingID: isInstantFormRequest ? "" : formValues.trackingID,
      trackingCustomEventID: isInstantFormRequest
        ? ""
        : formValues.trackingCustomEventID,
      catalogID: formValues.catalogID,
      destinationURL: isInstantFormRequest ? "" : formValues.destinationURL,
      typeBudget: formValues.typeBudget,
      budget: formValues.budget,
      startDate: formValues.startDate,
      endDate: formValues.endDate,
      mediaUrls: formValues.mediaUrls,
      platform: TIKTOK,
      appID: formValues.appID,
      isIOS14Campaign:
        formValues.type === ADS_CAMPAIGN_APP_PROMOTION_TYPE_RETARGETING_OPTION
          ? false
          : formValues.isIOS14Campaign,
      osTarget: formValues.appPromotion,
      type: formValues.type,
    };

    if (verifyBlockAlert) {
      const isBlocked = await dispatch(
        getIsBlockedActions({
          action: ADS_CAMPAIGN_GENERATE_CAMPAIGN_ACTION,
          showUsageConfirmationAlert: true,
          campaignUsageContext: {
            platform: TIKTOK,
            generatedPlatforms,
          },
        })
      );
      if (isBlocked) {
        return;
      }
    }

    void saveInitialPlatformDefaultsFromCampaign({
      dispatch,
      platform: TIKTOK,
      formValues: getValues(),
    });

    onCallbackSave(requestObject);
  };

  const { steps, fields } = useMemo(() => {
    const fieldsConfig = {
      objective: {
        isVisible: () => true,
        isFinishQuestion: false,
        utils,
        name: "objective",
        title: CREATE_ADS_CAMPAIGN_OBJECTIVE_QUESTION,
        subtitle: CREATE_ADS_CAMPAIGN_OBJECTIVE_DESCRIPTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const objective = getValues("objective");
          if (!objective || objective?.length === 0) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          filterMediaUrls();
          if (objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION) {
            setValue("type", ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_WEBSITE_OPTION);
          } else if (
            objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION
          ) {
            setValue("type", "");
          }
          setValue("step", step + 1);
        },
      },
      type: {
        isVisible: () =>
          objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION ||
          objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
        isFinishQuestion: false,
        utils,
        name: "type",
        title:
          objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION
            ? CREATE_ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_QUESTION
            : CREATE_ADS_CAMPAIGN_TYPE_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const type = getValues("type");
          if (!type || type?.length === 0) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          filterMediaUrls();
          setValue("step", step + 1);
        },
      },
      appPromotion: {
        isVisible: () =>
          objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION &&
          type === ADS_CAMPAIGN_APP_PROMOTION_TYPE_APP_INSTALL_OPTION,
        isFinishQuestion: false,
        utils,
        name: "appPromotion",
        title: CREATE_ADS_CAMPAIGN_APP_PROMOTION_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const appPromotion = getValues("appPromotion");
          if (!appPromotion) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      identities: {
        isVisible: () => true,
        isFinishQuestion: false,
        utils,
        name: "identities",
        title: CREATE_ADS_CAMPAIGN_IDENTITIES_ACCOUNTS_QUESTION,
        showActionsButtons: true,
        showPreviousButton: Boolean(onExitToPlatformSelection),
        onPreviousStep: () => {
          actionsToValidAnswer();
          onExitToPlatformSelection?.();
        },
        showNextButton: true,
        onNextStep: () => {
          const integrationID = getValues("integrationID");
          const adAccountID = getValues("adAccountID");
          const identityTikTokID = getValues("identityTikTokID");
          if (!adAccountID || !identityTikTokID || !integrationID) {
            setError(t(FIELDS_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      appID: {
        isVisible: () =>
          objective === ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION,
        isFinishQuestion: false,
        utils,
        name: "appID",
        title: CREATE_ADS_CAMPAIGN_APP_ID_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const appID = getValues("appID");
          if (!appID) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      leadDestination: {
        isVisible: () => objective === ADS_CAMPAIGN_OBJECTIVE_LEADS_OPTION,
        isFinishQuestion: false,
        utils,
        name: "leadDestination",
        title: CREATE_ADS_CAMPAIGN_TIKTOK_LEAD_DESTINATION_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const selectedDestination = getValues("destinationType");
          if (!selectedDestination) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      trackingID: {
        isVisible: () =>
          ADS_CAMPAIGN_OBJECTIVES_WITH_TRACKING_TIKTOK.includes(objective) &&
          destinationType !== ADS_CAMPAIGN_TIKTOK_DESTINATION_TYPE_INSTANT_PAGE,
        isFinishQuestion: false,
        utils,
        name: "trackingID",
        title: CREATE_ADS_CAMPAIGN_TRACKING_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const trackingID = getValues("trackingID");
          if (!trackingID) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      catalogID: {
        isVisible: () =>
          objective === ADS_CAMPAIGN_OBJECTIVE_SALES_OPTION &&
          type === ADS_CAMPAIGN_TIKTOK_CAMPAIGN_TYPE_APP_OPTION,
        isFinishQuestion: false,
        utils,
        name: "catalogID",
        title: CREATE_ADS_CAMPAIGN_CATALOG_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const catalogID = getValues("catalogID");
          if (!catalogID) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      destinationURL: {
        isVisible: () =>
          objective !== ADS_CAMPAIGN_OBJECTIVE_APP_PROMOTION_OPTION &&
          !isInstantFormLead,
        isFinishQuestion: false,
        utils,
        name: "destinationURL",
        title: CREATE_ADS_CAMPAIGN_DESTINATION_URL_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const destinationURL = getValues("destinationURL");
          if (!destinationURL) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          if (
            !REGEX_URL_WITH_PROTOCOL_REQUIRED.test(encodeURI(destinationURL))
          ) {
            setError(t(FIELD_URL_VALID_WITH_PROTOCOL));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      typeBudget: {
        isVisible: () => true,
        isFinishQuestion: false,
        utils,
        name: "typeBudget",
        title: CREATE_ADS_CAMPAIGN_TYPE_BUDGET_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const typeBudget = getValues("typeBudget");
          if (!typeBudget) {
            setError(t(FIELD_REQUIRED));
            return false;
          }
          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      budget: {
        isVisible: () => true,
        isFinishQuestion: false,
        utils,
        name: "budget",
        title:
          typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_DAILY_OPTION
            ? CREATE_ADS_CAMPAIGN_BUDGET_DAILY_QUESTION
            : CREATE_ADS_CAMPAIGN_BUDGET_TOTAL_QUESTION,
        subtitle: CREATE_ADS_CAMPAIGN_BUDGET_DESCRIPTION_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const budget = getValues("budget");
          if (!budget) {
            setError(t(FIELD_REQUIRED));
            return false;
          }

          const minimumBudget = getMinimumBudget({
            platform: TIKTOK,
            typeBudget,
            startDateTime: getValues("startDate"),
            endDateTime: getValues("endDate"),
            currency,
          });

          if (minimumBudget && budget < minimumBudget) {
            setError(
              t(FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR, {
                value: minimumBudget,
                currency: currency,
              })
            );
            return false;
          }

          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      dates: {
        isVisible: () =>
          typeBudget === ADS_CAMPAIGN_TYPE_BUDGET_LIFETIME_OPTION,
        isFinishQuestion: false,
        utils,
        name: "dates",
        title: CREATE_ADS_CAMPAIGN_START_END_DATE_QUESTION,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        showNextButton: true,
        onNextStep: () => {
          const startDate = getValues("startDate");
          const endDate = getValues("endDate");
          if (!startDate || !endDate) {
            setError(t(FIELDS_REQUIRED));
            return false;
          }

          if (startDate > endDate) {
            setError(t(START_DATE_AND_END_DATE_VALIDATION));
            return false;
          }

          actionsToValidAnswer();
          setValue("step", step + 1);
        },
      },
      mediaUrls: {
        isVisible: () => true,
        isFinishQuestion: true,
        disabledFinishButton: !mediaUrls || mediaUrls.length === 0,
        onFinish: () => {
          const mediaUrls = getValues("mediaUrls");
          if (!mediaUrls || mediaUrls.length === 0) {
            setError(t(FIELD_REQUIRED));
            return false;
          }

          const budget = getValues("budget");
          const minimumBudget = getMinimumBudget({
            platform: TIKTOK,
            typeBudget,
            startDateTime: getValues("startDate"),
            endDateTime: getValues("endDate"),
            currency,
          });

          if (minimumBudget && budget < minimumBudget) {
            setError(
              t(FIELD_MIN_TOTAL_BUDGET_ONE_DOLLAR, {
                value: minimumBudget,
                currency: currency,
              })
            );
            return false;
          }

          actionsToValidAnswer();
          onFinished();
        },
        utils,
        name: "mediaUrls",
        title: CREATE_ADS_CAMPAIGN_MEDIA_URL_QUESTION,
        subtitle: CREATE_ADS_CAMPAIGN_MEDIA_URL_DESCRIPTION_WITH_OUT_CAROUSEL,
        showActionsButtons: true,
        showPreviousButton: true,
        onPreviousStep: () => {
          actionsToValidAnswer();
          setValue("step", step - 1);
        },
        customComponentPreviousNext: (
          <Controller
            name="mediaUrls"
            control={control}
            render={({ field }) => (
              <IconButtonAddContent
                mode="button"
                color="secondary"
                size="large"
                minDimensions={MIN_DIMENSIONS_CONTENT}
                pathStorage={`adsCampaigns/${userID}/`}
                pathRTDB={`adsContentsUploaded/${userID}/${campaignID}`}
                modeUpload="sync"
                onAddContent={(contents) => {
                  // Regular items: format each one
                  const contentsFormatted = contents.map((content) =>
                    formatMediaUrl(content)
                  );
                  field.onChange([...field.value, ...contentsFormatted]);
                }}
                onCallbackSaveAI={(videoData) => {
                  field.onChange([
                    ...field.value,
                    formatMediaUrlAdFromCreatify(videoData),
                  ]);
                }}
                initialValues={{ language, campaignContext, platform: TIKTOK }}
                onCallbackSaveCanva={(mediaUrl) => {
                  const canvaContent = formatMediaUrl({
                    url: mediaUrl,
                    type: "upload",
                  });
                  field.onChange([...field.value, canvaContent]);
                }}
                multiple={true}
                acceptedFormats={VIDEO_FORMATS.concat(
                  isCarouselAdsAvailable
                    ? [ADS_CAMPAIGN_CAROUSEL_FORMAT, ...TIKTOK_IMAGE_FORMATS]
                    : []
                )}
                textButton={BUTTON_SELECT_CONTENT}
                sx={{
                  alignItems: "flex-start",
                  width: { xs: "100%", md: "auto" },
                }}
                sxButton={{
                  height: 48,
                  width: "100%",
                }}
              />
            )}
          />
        ),
        showNextButton: false,
        onNextStep: () => {
          setValue("step", step + 1);
        },
      },
    };

    const stepsVisible = STEPS.filter((step) => fieldsConfig[step].isVisible());

    return {
      steps: stepsVisible,
      fields: fieldsConfig,
    };

    // eslint-disable-next-line
  }, [
    step,
    objective,
    type,
    typeBudget,
    mediaUrls,
    destinationType,
    language,
    onExitToPlatformSelection,
  ]);

  const { hasJumped } = useJumpToLastVisibleStep({
    enabled: Boolean(resumeValues),
    ready: resumeSettled,
    steps,
    setValue,
  });
  const isApplyingResume = Boolean(resumeValues) && !hasJumped;
  const currentAnswer = isApplyingResume ? null : steps[step];
  const totalSteps = steps.length;
  const progress = Math.round(((step + 1) / totalSteps) * 100);

  return {
    steps,
    fields,
    utils,
    currentAnswer,
    error,
    progress,
    currency,
    isLoadingDefaults,
  };
}
